import React, { useState } from "react";
import { useTheme } from "../../theme";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";

// Define __DEV__ if it doesn't exist (for web environments)
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { createApiClient, apiClient ,getLocalTimestamp} from "../../api/apiClient";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";
import {
  resetPulseCheck,
  resetManagerSignatureFlag,
  resetEmployeeSignatureFlag,
} from "../../store/slices/pulseCheckSlice";
import { setSuccessMessage, resetObservation } from "../../store/slices/observationSlice";
import { useToast } from "../../components/LhToastContainer";
import FontFamily from "../../constants/fonts";

interface PulseCheckActionProps {
  onSave?: () => void;
  onComplete: () => boolean; // Updated to return boolean for validation result
  onBackToObservation?: () => void;
  onShowMessage?: (type: "success" | "error", message: string) => void;
  forceDisabled?: boolean;
}

const PulseCheckAction: React.FC<PulseCheckActionProps> = ({
  onComplete,
  onBackToObservation,
  onShowMessage,
  forceDisabled = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const pdfHelperColor = isDark ? '#FFFFFF' : '#777777';
  const dispatch = useDispatch();
  const toast = useToast();

  // Detect wide screens (web or tablet/iPad). Treat width >= 768 as tablet breakpoint.
  const { width } = useWindowDimensions();
  const isTabletOrWeb = Platform.OS === "web" || width >= 768;

  // Get data from Redux state
  const selectedOptions = useSelector(
    (state: RootState) => state.pulseCheck.selectedOptions
  );

  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );

  const selectedReferenceQuestions = useSelector(
    (state: RootState) => state.pulseCheck.selectedReferenceQuestions
  );

  const formValues = useSelector(
    (state: RootState) => state.pulseCheck.formValues
  );

  const notes = useSelector((state: RootState) => state.pulseCheck.notes);
  const metGoal = useSelector((state: RootState) => state.pulseCheck.metGoal);

  // Get signature data from Redux state
  const managerSignature = useSelector((state: RootState) => ({
    fileName: state.pulseCheck.managerSignatureFileName,
    imageUrl: state.pulseCheck.managerSignatureImageUrl,
    isUpdated: state.pulseCheck.isManagerSignatureUpdated,
  }));

  const employeeSignature = useSelector((state: RootState) => ({
    fileName: state.pulseCheck.employeeSignatureFileName,
    imageUrl: state.pulseCheck.employeeSignatureImageUrl,
    isUpdated: state.pulseCheck.isEmployeeSignatureUpdated,
  }));

  const selectedFilters = useSelector(
    (state: RootState) => state.observation.selectedFilters
  );

  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  const period = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.period
  );

  const week = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.week
  ); // Get user information from Redux auth state for Created_By and Modified_By fields
  const userEmail = useSelector(
    (state: RootState) => state.auth.user?.username
  );

  // Get selectedObservation to access observationNotes
  const selectedObservation = useSelector(
    (state: RootState) => state.observation.selectedObservation
  );

  const selectedRecord = useSelector(
    (state: RootState) => state.pulseCheck.selectedRecord
  );
  const selectedRecordId = selectedRecord?.id || "";

  // Get miscellaneous observation images and descriptions from Redux store
  const observationImages = useSelector(
    (state: RootState) => state.pulseCheck.observationImages
  );
  const miscImageDescription = useSelector(
    (state: RootState) => state.pulseCheck.miscImageDescription
  );

  // Get pulse image data from Redux store
  const pulseImages = useSelector(
    (state: RootState) => state.pulseCheck.pulseImages
  );

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    if (isPdfExportDisabled || !selectedRecordId || !userEmail) {
      return;
    }

    try {
      setIsPdfExporting(true);

      // Build the subject line
      const recordName =
        selectedRecord?.name ||
        (selectedFilters.employeeFirstName && selectedFilters.employeeLastName
          ? `${selectedFilters.employeeFirstName} ${selectedFilters.employeeLastName}`
          : "Unknown");
      const subjectLine = `Observation Report for ${recordName} - ${selectedRecordId}`;

      // Build the API URL with query parameters
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/notifications/email/pdf?emailTo=${encodeURIComponent(
          userEmail
        )}&inspection_module=observation&id=${selectedRecordId}&subject=${encodeURIComponent(
          subjectLine
        )}`,
        "GET"
      );

      // Handle the response
      if (response && response.message) {
        toast.showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
      } else {
        toast.showToast({
          header: "Email Error",
          subtext: "Unknown error occurred while sending email",
          variant: "error",
        });
      }
    } catch (error: any) {
      console.error("PDF Export Error:", error);

      // Show error toast notification
      toast.showToast({
        header: "Email Error",
        subtext: error?.message || "Failed to send PDF export email",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: string) => {
    try {
      // Only validate all fields (including signatures) for 'complete' action
      if (action === "complete") {
        // Call validation function provided by parent component
        const isValid = onComplete();

        // If validation failed, exit early
        if (!isValid) {
          return;
        }
      }

      // Set submitting state to true to disable both buttons
      setIsSubmitting(true);

      // Show a progress message at the top of the screen
      // if (onShowMessage) {
      //   onShowMessage('success', 'Submitting observation... Please wait');
      // }      // Format user name from email (e.g., "Joshua.Jabakumar.Contractor@pepsico.com" -> "Joshua Jabakumar")
      // Helper to format user name from email for display (for use in UI tables)
      // Helper to format user name from email for display (for use in UI tables)
      // Use this in your datatable display logic, not here in the payload
      interface PulseCheckPayload {
        ee: {
          firstName: string;
          lastName: string;
          gpid: string;
        };
        selectedItems: { observationId: number }[];
        selectedQuestions: { questionId: number }[];
        observationData: {
          metricsDiscussed: string;
          lastShiftResults: string;
          targetResults: string;
          firstObjective: string;
          metricsDiscussed2: string;
          actualResults: string;
          metGoal: string;
          secondObjectiveResult: string;
          type: string;
          roleObserved: string;
          shiftNumber: string;
          observationType: string;
          status: string;
          period?: number;
          week?: number;
          observationNotes?: string;
          miscellImageDesc?: string;
          miscellImage2Desc?: string;
          miscellImage?: string;
          miscellImage2?: string;
          updatedImg1?: boolean;
          updatedImg2?: boolean;
          Manager_Signature?: string;
          Employee_Signature?: string;
          updatedManagerSignature?: boolean;
          updatedEmployeeSignature?: boolean;
        };
        location: string;
        oneSubmission?: boolean;
        selectedRecord?: { id: string };
        createdBy?: string;
        created?: string;
        modifiedBy?: string;
        modified?: string;
        images?: {
          imageId: string;
          imageBase64: string;
          description: string;
          observationId: string;
        }[];
      }

      // Prepare the API request payload
      const payload: PulseCheckPayload = {
        ee: {
          firstName:
            selectedFilters.employeeFirstName ||
            selectedRecord.rawData.EE_First_Name ||
            "",
          lastName:
            selectedFilters.employeeLastName ||
            selectedRecord.rawData.EE_Last_Name ||
            "",
          gpid: selectedFilters.employeeGpid || "",
        },
        selectedItems: selectedOptions.map((option) => ({
          observationId: parseInt(option.id, 10) || 0,
        })),
        selectedQuestions: selectedReferenceQuestions.map((question) => ({
          questionId: parseInt(question.id, 10) || 0,
        })),
        observationData: {
          // First Check
          metricsDiscussed: formValues.metric1,
          lastShiftResults: formValues.result1,
          targetResults: formValues.target1,
          firstObjective: notes.first,

          // Second Check
          metricsDiscussed2: formValues.metric2,
          actualResults: formValues.result2,
          metGoal: metGoal === true ? "Success" : metGoal === false ? "Failure" : "Not Selected",
          secondObjectiveResult: notes.second,

          // Additional data from filters
          type: selectedFilters.observationType || "",
          roleObserved: selectedFilters.role || "",
          shiftNumber: selectedFilters.shift || "",
          observationType: selectedFilters.observationType || "",
          status: action === "save" ? "In Progress" : "Completed",
          period: period ? parseInt(String(period), 10) : undefined,
          week: week ? parseInt(String(week), 10) : undefined,

          // Add observationNotes for full and routine observation types
          observationNotes:
            selectedFilters.observationType !== "Pulse Check"
              ? selectedObservation?.observationNotes || ""
              : undefined,

          // Miscellaneous Observation image descriptions from Redux store
          miscellImageDesc: miscImageDescription?.description1 || "",
          miscellImage2Desc: miscImageDescription?.description2 || "",

          // Miscellaneous Observation image filenames from Redux store
          miscellImage:
            (observationImages && observationImages[0]?.fileName) || "",
          miscellImage2:
            (observationImages && observationImages[1]?.fileName) || "",

          // Determine if images were updated by comparing with selectedRecord
          updatedImg1: Boolean(
            observationImages &&
              observationImages[0]?.imageUrl &&
              selectedRecord?.observationData?.miscellImage !==
                observationImages[0].imageUrl
          ),
          updatedImg2: Boolean(
            observationImages &&
              observationImages[1]?.imageUrl &&
              selectedRecord?.observationData?.miscellImage2 !==
                observationImages[1].imageUrl
          ),

          // Add signature data to payload
          Manager_Signature: managerSignature.fileName || "",
          Employee_Signature: employeeSignature.fileName || "",
          updatedManagerSignature: managerSignature.isUpdated,
          updatedEmployeeSignature: employeeSignature.isUpdated,
        },
        location: selectedLocation?.name || selectedLocation?.displayText || "",
      };

      // If the pulseState is "New", we set the createdBy and created fields from the user
      if (pulseState === "new") {
        payload.created = getLocalTimestamp()
        payload.createdBy = userEmail || "";
        payload.modifiedBy = userEmail || "";
        payload.modified = getLocalTimestamp()
      }

      // If the pulseState is "In Progress", we set the createdBy and created fields from the selectedRecord
      if (pulseState === "in-progress") {
        // Update ee fields with selectedRecord data if available
        payload.ee.firstName =
          selectedRecord?.rawData?.EE_First_Name || payload.ee.firstName;
        payload.ee.lastName =
          selectedRecord?.rawData?.EE_Last_Name || payload.ee.lastName;
        payload.ee.gpid = selectedRecord?.rawData?.GPID || payload.ee.gpid;

        payload.createdBy = selectedRecord?.createdBy || userEmail || "";

        // Safely handle date parsing to prevent "Date value out of bounds" error
        try {
          if (selectedRecord?.created) {
            const createdDate = new Date(selectedRecord.created);
            // Check if the date is valid before calling toISOString()
            if (!isNaN(createdDate.getTime())) {
              payload.created = createdDate.toISOString();
            } else {
              payload.created = getLocalTimestamp()
            }
          } else {
            payload.created = getLocalTimestamp()
          }
        } catch (dateError) {
          console.log("Error parsing created date:", dateError);
          payload.created = getLocalTimestamp()
        }

        payload.modifiedBy = userEmail || "";
        payload.modified = getLocalTimestamp()
      }

      if (selectedRecordId) {
        payload.oneSubmission = true;
        payload.selectedRecord = {
          id: selectedRecordId,
        };
      }

      // Add pulse images to the payload (excluding imagePreview field)
      if (pulseImages && pulseImages.length > 0) {
        payload.images = pulseImages.map((img) => ({
          imageId: img.imageId,
          imageBase64: img.imageBase64 || img.imagePreview,
          description: img.description,
          observationId: img.observationId,
        }));
      }

      // Log image data being sent
      if (__DEV__) {
        console.log("Image Data in Payload:", {
          miscellImageDesc: payload.observationData.miscellImageDesc,
          miscellImage2Desc: payload.observationData.miscellImage2Desc,
          miscellImage: payload.observationData.miscellImage,
          miscellImage2: payload.observationData.miscellImage2,
          updatedImg1: payload.observationData.updatedImg1,
          updatedImg2: payload.observationData.updatedImg2,
          Manager_Signature: payload.observationData.Manager_Signature,
          Employee_Signature: payload.observationData.Employee_Signature,
          updatedManagerSignature:
            payload.observationData.updatedManagerSignature,
          updatedEmployeeSignature:
            payload.observationData.updatedEmployeeSignature,
        }); // Log the raw image data from Redux for debugging
        console.log("Raw image data from Redux:", {
          image1: observationImages?.[0]
            ? {
                url: observationImages[0].imageUrl,
                fileName: observationImages[0].fileName,
              }
            : "No image 1",
          image2: observationImages?.[1]
            ? {
                url: observationImages[1].imageUrl,
                fileName: observationImages[1].fileName,
              }
            : "No image 2",
        });

        // Log pulseImages data for debugging
        console.log(
          "PulseImages data being sent:",
          pulseImages.map((img) => ({
            imageId: img.imageId,
            imageBase64: img.imageBase64 || img.imagePreview,
            description: img.description,
            observationId: img.observationId,
          }))
        );

        // Log signature data from Redux
        console.log("Signature data from Redux:", {
          managerSignature: {
            fileName: managerSignature.fileName,
            imageUrl: managerSignature.imageUrl,
            isUpdated: managerSignature.isUpdated,
          },
          employeeSignature: {
            fileName: employeeSignature.fileName,
            imageUrl: employeeSignature.imageUrl,
            isUpdated: employeeSignature.isUpdated,
          },
        });
      }

      // Make the API call
      const api = createApiClient();
      const response = await apiClient(
        api,
        "/observations/submit",
        "POST",
        payload
      );

      // Handle the API response
      if (response && (response.success || response.status === "success")) {
        // Extract data from response
        const observationId = response.data?.observationId;
        // Create success message with ticket number if available
        const successMsg = observationId
          ? "Observation created successfully."
          : "Observation created successfully";

        // Reset the signature flags before the full reset to ensure clean state
        if (managerSignature.isUpdated) {
          console.log("Resetting manager signature flag");
          dispatch(resetManagerSignatureFlag());
        }

        if (employeeSignature.isUpdated) {
          console.log("Resetting employee signature flag");
          dispatch(resetEmployeeSignatureFlag());
        }

        // Reset the pulse check data immediately on success
        console.log("Resetting pulse check data after successful API response");
        dispatch(resetPulseCheck());

        // Reset the selected observation to clear observation notes and other data
        console.log("Resetting selected observation after successful API response");
        dispatch(resetObservation());

        // Set success message in Redux store for Observation screen to display
        dispatch(setSuccessMessage(successMsg));

        // Log success for debugging
        console.log(
          "API call successful, navigating back to Observation screen"
        );

        // For successful submissions, immediately navigate back to Observation screen
        // without showing alert dialog, as we'll display the success message there
        if (onBackToObservation) {
          onBackToObservation();
        }
      } else {
        // Handle error responses based on the provided formats
        let errorTitle = "Error";
        let errorMessage = "Failed to submit observation";

        if (response?.status === "error") {
          // Handle both validation and server errors
          errorMessage = response.message || errorMessage;

          if (response.message === "Validation failed" && response.details) {
            // If it's a validation error with details
            errorTitle = "Validation Error";
            // Format validation details if they exist
            const detailsStr = Object.entries(response.details || {})
              .map(([field, msg]) => `${field}: ${msg}`)
              .join("\n");

            if (detailsStr) {
              errorMessage = `${errorMessage}\n${detailsStr}`;
            }
          } else if (
            response.message?.includes("Database") ||
            response.message?.includes("server")
          ) {
            // If it's a database or server error
            errorTitle = "Server Error";
          }
        } else if (response?.message) {
          errorMessage = response.message;
        }

        // Show error message at the top of PulseCheckScreen if onShowMessage is available
        if (onShowMessage) {
          onShowMessage("error", errorMessage);
        }

        // Also show centralized overlay with API error details
        try {
          const overlayMsg = "Unable to submit observation. Please try again.";
          const overlayErrorPayload = overlayMsg; // force friendly message in overlay
          handleObservationErrorWithFriendlyMessage(
            OBSERVATION_API_NAMES.SUBMIT_OBSERVATION,
            overlayErrorPayload,
            true,
            { response }
          );
        } catch (e) {
          console.error('Failed to show centralized error overlay:', e);
        }

        // For errors, we stay on the current screen and show the error message at the top
        console.log("API error:", errorTitle, errorMessage);
      }
    } catch (error: any) {
      console.error("API Error:", error);

      // Attempt to parse error response from the caught error
      const errorMessage =
        (error?.response?.data?.status === "error"
          ? error.response.data.message
          : null) ||
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while submitting the observation";

      // Determine error type from message
      const errorTitle = errorMessage.includes("Validation")
        ? "Validation Error"
        : errorMessage.includes("Database") || errorMessage.includes("server")
        ? "Server Error"
        : "Error";
      // Show error message at the top of PulseCheckScreen if onShowMessage is available
      if (onShowMessage) {
        onShowMessage("error", errorMessage);
      }

      // Show centralized overlay for submit API
      try {
        const overlayMsg = "Unable to submit observation. Please try again.";
        handleObservationErrorWithFriendlyMessage(
          OBSERVATION_API_NAMES.SUBMIT_OBSERVATION,
          overlayMsg,
          true,
          { originalError: error }
        );
      } catch (e) {
        console.error('Failed to show centralized error overlay in catch:', e);
      }

      // For errors, we stay on the current screen and show the error message at the top
      console.log("Exception caught:", errorTitle, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }; // Determine if the PDF export box should be disabled
  const isPdfExportDisabled =
    forceDisabled || pulseState === "new" || pulseState === "in-progress";
  return (
    <View style={[styles.actionRow, isTabletOrWeb ? styles.actionRowWide : null]}>
      <View style={[styles.componentWrapper, isTabletOrWeb ? styles.componentWrapperWide : null]}>
        <TouchableOpacity
          style={[
            isPdfExportDisabled
              ? styles.disabledButton
              : styles.pdfExportButton,
            // Apply fixed width and layout for tablet/web
            isTabletOrWeb ? styles.buttonFixedWidth : {},
            Platform.OS === "web" && !isPdfExportDisabled
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
          disabled={isPdfExportDisabled}
          accessibilityState={{ disabled: isPdfExportDisabled }}
          accessibilityLabel={
            isPdfExportDisabled
              ? "PDF Export Not Available"
              : isPdfExporting
              ? "Sending PDF export email"
              : "PDF Export"
          }
        >
          <View style={styles.buttonContent}>
            {isPdfExportDisabled ? (
              <>
                <MaterialIcons
                  name="picture-as-pdf"
                  size={24}
                  color="#B1B1B1"
                  style={{marginRight: 8}}
                />
                <Text
                  style={styles.buttonTextDisabled}
                >
                  PDF Export
                </Text>
              </>
            ) : isPdfExporting ? (
              <>
                <ActivityIndicator
                  size="small"
                  color="#FFF"
                  style={{marginRight: 8}}
                />
                <Text style={styles.buttonText}>
                  Sending Email...
                </Text>
              </>
            ) : (
              <>
                <MaterialIcons
                  name="picture-as-pdf"
                  size={24}
                  color="white"
                  style={{marginRight: 8}}
                />
                <Text style={styles.buttonText}>
                  PDF Export
                </Text>
              </>
            )}
          </View>
        </TouchableOpacity>
        {isPdfExportDisabled && (
          <Text
            style={{
              color: pdfHelperColor,
              fontSize: 12,
              fontFamily: FontFamily.regular,
              fontWeight: "400",
              fontStyle: "normal",
              lineHeight: 16,
              letterSpacing: 0,
              marginTop: 8,
              textAlign: "center",
              maxWidth: 189,
            }}
          >
            PDF export is available after saving or completing
          </Text>
        )}
      </View>
      <View style={[styles.buttonColumn, isTabletOrWeb ? styles.buttonColumnWide : null]}>
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            // Make buttons align and size like web on tablet
            isTabletOrWeb ? styles.buttonFlexWide : {},
            isSubmitting || pulseState === "completed"
              ? styles.disabledButton
              : styles.saveButton,
            // Add cursor pointer only when enabled and on web platform
            isTabletOrWeb &&
            !(isSubmitting || pulseState === "completed")
              ? { cursor: "pointer" as any }
              : {},
            // Fixed width on tablet/web to match web layout
            isTabletOrWeb ? styles.buttonFixedWidth : {},
          ]}
          onPress={() => handleSubmit("save")}
          disabled={forceDisabled || isSubmitting || pulseState === "completed"}
          accessibilityState={{
            disabled:
              forceDisabled || isSubmitting || pulseState === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Save for later (disabled during submission)"
              : pulseState === "completed"
              ? "Save for later (disabled for completed pulse check)"
              : "Save for later"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#034895"
                style={{marginRight: 8}}
              />
            ) : (
              <>
                <MaterialIcons
                  name="save"
                  size={24}
                  color={isSubmitting || pulseState === "completed" ? "#B1B1B1" : "#034895"}
                  style={{marginRight: 8}}
                />
                <Text
                  style={isSubmitting || pulseState === "completed" ? styles.buttonTextDisabled : styles.saveButtonText}
                >
                  Save for Later
                </Text>
              </>
            )}
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isTabletOrWeb ? styles.buttonFlexWide : {},
            isSubmitting || pulseState === "completed"
              ? styles.disabledButton
              : styles.completeButton,
            // Add cursor pointer only when enabled and on web platform
            isTabletOrWeb &&
            !(isSubmitting || pulseState === "completed")
              ? { cursor: "pointer" as any }
              : {},
            // Fixed width on tablet/web to match web layout
            isTabletOrWeb ? styles.buttonFixedWidth : {},
          ]}
          onPress={() => handleSubmit("complete")}
          disabled={isSubmitting || pulseState === "completed"}
          accessibilityState={{
            disabled: isSubmitting || pulseState === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Complete form (disabled during submission)"
              : pulseState === "completed"
              ? "Complete form (disabled for completed pulse check)"
              : "Complete form"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#B1B1B1"
                style={{marginRight: 8}}
              />
              ) : (
                <Text style={isSubmitting || pulseState === "completed" ? styles.buttonTextDisabled : styles.buttonText}>
                  Complete
                </Text>
              )}
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: "column",
    flexWrap: "wrap",
    marginTop: 30,
    // borderTopWidth: 1,
    borderTopColor: "#dee2e6",
    paddingTop: 15,
    paddingBottom: 16,
    // gap: 24,
    alignItems: Platform.OS === "web" ? "flex-start" : "center",
    justifyContent: "flex-start",
    width: "100%", // Ensure full width
  },
  // Wide layout overrides applied for tablet / web
  actionRowWide: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  errorContainer: {
    backgroundColor: "#ffdddd",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#ff8888",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 14,
    textAlign: "center",
  },
  componentWrapper: {
    flex: undefined,
    marginRight: 0,
    minWidth: "100%",
    width: "100%",
    marginBottom: 10,
  },
  componentWrapperWide: {
    flex: 0,
    marginRight: 20,
    minWidth: 189,
    width: "auto",
    marginBottom: 0,
  },
  buttonColumn: {
    flex: undefined,
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: 16,
    minWidth: "100%",
    width: "100%",
  },
  buttonColumnWide: {
    flex: 0,
    flexDirection: "row",
    minWidth: 394,
    width: "auto",
    justifyContent: "flex-end",
  },
  buttonFlex: {
    flex: 0,
    minWidth: 0, // Allow buttons to shrink if needed
    width: Platform.OS === "web" ? "auto" : "100%",
  },
  buttonFlexWide: {
    flex: 1,
    width: "auto",
  },
  customButton: {
    paddingVertical: 8, // Consistent vertical padding
    paddingRight: 24,
    paddingLeft :15, // Consistent horizontal padding
    borderRadius: 24,
    backgroundColor: "#034895",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 120,
    height: 40, // Consistent height
    alignItems: "center",
    justifyContent: "center",
  },
  saveButton: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    borderWidth: 1,
    borderColor: "#034895",
    paddingVertical: 8,
     paddingRight: 15,
    paddingLeft :15, 
    // paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === "web" ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  completeButton: {
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 8,
    // paddingHorizontal: 24,
     paddingRight: 24,
    paddingLeft :15, 
    height: 40,
    width: Platform.OS === "web" ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  pdfExportButton: {
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 8,
    // paddingHorizontal: 24,
     paddingRight: 24,
    paddingLeft :15, 
    height: 40,
    width: Platform.OS === "web" ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: "#F5F5F5", // Use a light grey for disabled state (not white)
    borderWidth: 1,
    borderColor: "#DADADA",
    borderRadius: 24,
    paddingVertical: 8,
    // paddingHorizontal: 24,
     paddingRight: 15,
    paddingLeft :15, 
    height: 40,
    width: Platform.OS === "web" ? 189 : "100%", // Match width with other buttons
    shadowOpacity: 0.1 as number,
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: 8,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    fontStyle: "normal",
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  saveButtonText: {
    color: "#034895",
    fontSize: 16,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    fontStyle: "normal", 
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  buttonTextDisabled: {
    color: "#B1B1B1",
    fontSize: 16,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    fontStyle: "normal",
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  // Fixed width used on tablet/web to mirror web layout
  buttonFixedWidth: {
    width: 189,
  },
});

export default PulseCheckAction;
